import React, { useCallback, useEffect, useState } from "react";
import { apiClient, formatApiError } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { Loader2, Search, ShieldCheck, XCircle, Ban, CheckCircle2, ClipboardList } from "lucide-react";

const ACCT_TABS = ["all", "pending", "verified", "rejected", "suspended"];
const WD_TABS = ["all", "pending", "approved", "rejected", "paid"];

/**
 * Admin — Payouts (IBAN accounts + withdrawals + audit log). Additive only.
 */
export default function PayoutsAdminPage() {
  const { lang } = useI18n();
  const [tab, setTab] = useState("accounts"); // accounts | withdrawals | audit
  return (
    <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-8" data-testid="admin-payouts-page">
      <div className="flex items-baseline justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="overline overline-gold">{lang === "ar" ? "لوحة الإدارة" : "ADMIN"}</div>
          <h1 className="font-display text-3xl font-medium tracking-tight text-[#0a0a0a]">
            {lang === "ar" ? "إدارة المدفوعات والسحوبات" : "Payouts management"}
          </h1>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {[
          { id: "accounts", ar: "حسابات الاستلام", en: "Payout accounts" },
          { id: "withdrawals", ar: "طلبات السحب", en: "Withdrawal requests" },
          { id: "audit", ar: "سجل التدقيق", en: "Audit log" },
        ].map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)}
                  className={`rounded-full px-4 py-2 text-sm font-bold transition-colors ${
                    tab === t.id ? "bg-[#0a0a0a] text-[#c9a55c]" : "border border-[#0a0a0a]/15 text-[#3a3a3a] hover:border-[#c9a55c]"
                  }`}
                  data-testid={`admin-payouts-tab-${t.id}`}>
            {lang === "ar" ? t.ar : t.en}
          </button>
        ))}
      </div>

      {tab === "accounts" && <AccountsPanel lang={lang} />}
      {tab === "withdrawals" && <WithdrawalsPanel lang={lang} />}
      {tab === "audit" && <AuditPanel lang={lang} />}
    </div>
  );
}

// ── ACCOUNTS ────────────────────────────────────────────────────────────
function AccountsPanel({ lang }) {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (status !== "all") params.status = status;
      if (q) params.q = q;
      const { data } = await apiClient.get("/admin/payouts/accounts", { params });
      setRows(data.items || []);
    } finally { setLoading(false); }
  }, [status, q]);

  useEffect(() => { load(); }, [load]);

  const review = async (a, next) => {
    const notes = window.prompt(
      lang === "ar" ? "ملاحظات (اختياري):" : "Notes (optional):", "",
    );
    if (notes === null) return;
    try {
      await apiClient.post(`/admin/payouts/accounts/${a.id}/review`,
                            { status: next, notes: notes || null });
      await load();
    } catch (e) { alert(formatApiError(e)); }
  };

  return (
    <>
      <div className="flex flex-wrap gap-2 mb-3">
        {ACCT_TABS.map((s) => (
          <button key={s} onClick={() => setStatus(s)}
                  className={`rounded-full px-3 py-1 text-xs font-semibold ${
                    status === s ? "bg-[#c9a55c] text-[#0a0a0a]" : "border border-[#0a0a0a]/15 hover:border-[#c9a55c]"
                  }`}>
            {s === "all" ? (lang === "ar" ? "الكل" : "All") : s}
          </button>
        ))}
      </div>

      <div className="relative mb-4 max-w-md">
        <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8a8a8a]" />
        <input value={q} onChange={(e) => setQ(e.target.value)}
               onKeyDown={(e) => e.key === "Enter" && load()}
               className="input-dark ps-10 text-sm" placeholder={lang === "ar" ? "بحث…" : "Search…"} />
      </div>

      {loading ? (
        <div className="py-10 text-center text-[#6b6b6b]"><Loader2 className="w-5 h-5 animate-spin inline me-2" />{lang === "ar" ? "تحميل…" : "Loading…"}</div>
      ) : (
        <div className="rounded-2xl border border-[#0a0a0a]/10 bg-white overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-[#fafaf7] text-[10px] uppercase tracking-[0.2em] text-[#6b6b6b]">
              <tr>
                <th className="text-start p-3">{lang === "ar" ? "التاريخ" : "Date"}</th>
                <th className="text-start p-3">{lang === "ar" ? "الاسم" : "Holder"}</th>
                <th className="text-start p-3">{lang === "ar" ? "البنك" : "Bank"}</th>
                <th className="text-start p-3">IBAN</th>
                <th className="text-start p-3">{lang === "ar" ? "الحالة" : "Status"}</th>
                <th className="text-start p-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan="6" className="p-10 text-center text-[#8a8a8a]">{lang === "ar" ? "لا توجد نتائج." : "No results."}</td></tr>
              ) : rows.map((a) => (
                <tr key={a.id} className="border-b border-[#0a0a0a]/5" data-testid={`admin-account-${a.id}`}>
                  <td className="p-3 text-xs" dir="ltr">{a.created_at?.slice(0,19).replace("T"," ")}</td>
                  <td className="p-3 font-semibold">{a.account_holder_name}</td>
                  <td className="p-3">{a.bank_name}</td>
                  <td className="p-3 font-mono text-xs" dir="ltr">{a.iban_masked}</td>
                  <td className="p-3"><StatusPill status={a.status} lang={lang} /></td>
                  <td className="p-3">
                    <div className="flex gap-2 flex-wrap">
                      {a.status !== "verified" && (
                        <button onClick={() => review(a, "verified")}
                                className="inline-flex items-center gap-1 text-xs font-bold text-green-700 hover:text-green-900"
                                data-testid={`account-verify-${a.id}`}>
                          <CheckCircle2 className="w-3 h-3" />{lang === "ar" ? "توثيق" : "Verify"}
                        </button>
                      )}
                      {a.status !== "rejected" && (
                        <button onClick={() => review(a, "rejected")}
                                className="inline-flex items-center gap-1 text-xs font-bold text-red-700 hover:text-red-900">
                          <XCircle className="w-3 h-3" />{lang === "ar" ? "رفض" : "Reject"}
                        </button>
                      )}
                      {a.status !== "suspended" && (
                        <button onClick={() => review(a, "suspended")}
                                className="inline-flex items-center gap-1 text-xs font-bold text-purple-700 hover:text-purple-900">
                          <Ban className="w-3 h-3" />{lang === "ar" ? "تعليق" : "Suspend"}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ── WITHDRAWALS ─────────────────────────────────────────────────────────
function WithdrawalsPanel({ lang }) {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (status !== "all") params.status = status;
      const { data } = await apiClient.get("/admin/payouts/withdrawals", { params });
      setRows(data.items || []);
    } finally { setLoading(false); }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const transition = async (w, next) => {
    let ref = null;
    let notes = window.prompt(lang === "ar" ? "ملاحظات (اختياري):" : "Admin notes (optional):", "");
    if (notes === null) return;
    if (next === "paid") {
      ref = window.prompt(lang === "ar" ? "رقم مرجع التحويل:" : "Bank transfer reference:", "");
      if (!ref) return;
    }
    try {
      await apiClient.post(`/admin/payouts/withdrawals/${w.id}/transition`, {
        status: next, admin_notes: notes || null, payment_reference: ref || null,
      });
      await load();
    } catch (e) { alert(formatApiError(e)); }
  };

  return (
    <>
      <div className="flex flex-wrap gap-2 mb-4">
        {WD_TABS.map((s) => (
          <button key={s} onClick={() => setStatus(s)}
                  className={`rounded-full px-3 py-1 text-xs font-semibold ${
                    status === s ? "bg-[#c9a55c] text-[#0a0a0a]" : "border border-[#0a0a0a]/15 hover:border-[#c9a55c]"
                  }`}>
            {s === "all" ? (lang === "ar" ? "الكل" : "All") : s}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-10 text-center text-[#6b6b6b]"><Loader2 className="w-5 h-5 animate-spin inline me-2" />{lang === "ar" ? "تحميل…" : "Loading…"}</div>
      ) : (
        <div className="rounded-2xl border border-[#0a0a0a]/10 bg-white overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-[#fafaf7] text-[10px] uppercase tracking-[0.2em] text-[#6b6b6b]">
              <tr>
                <th className="text-start p-3">{lang === "ar" ? "التاريخ" : "Date"}</th>
                <th className="text-start p-3">{lang === "ar" ? "المستخدم" : "User"}</th>
                <th className="text-start p-3">{lang === "ar" ? "المبلغ" : "Amount"}</th>
                <th className="text-start p-3">IBAN</th>
                <th className="text-start p-3">{lang === "ar" ? "الحالة" : "Status"}</th>
                <th className="text-start p-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan="6" className="p-10 text-center text-[#8a8a8a]">{lang === "ar" ? "لا توجد نتائج." : "No results."}</td></tr>
              ) : rows.map((w) => (
                <tr key={w.id} className="border-b border-[#0a0a0a]/5" data-testid={`admin-wd-${w.id}`}>
                  <td className="p-3 text-xs" dir="ltr">{w.created_at?.slice(0,19).replace("T"," ")}</td>
                  <td className="p-3">
                    <div className="font-semibold">{w.user_name}</div>
                    <div className="text-[10px] text-[#8a8a8a]">{w.user_email}</div>
                  </td>
                  <td className="p-3 font-mono font-bold" dir="ltr">{Number(w.amount_sar).toLocaleString()} SAR</td>
                  <td className="p-3 font-mono text-xs" dir="ltr">{w.iban_masked}</td>
                  <td className="p-3"><StatusPill status={w.status} lang={lang} /></td>
                  <td className="p-3">
                    <div className="flex gap-2 flex-wrap">
                      {w.status === "pending" && (
                        <>
                          <button onClick={() => transition(w, "approved")}
                                  className="inline-flex items-center gap-1 text-xs font-bold text-blue-700 hover:text-blue-900"
                                  data-testid={`wd-approve-${w.id}`}>
                            <ShieldCheck className="w-3 h-3" />{lang === "ar" ? "اعتماد" : "Approve"}
                          </button>
                          <button onClick={() => transition(w, "rejected")}
                                  className="inline-flex items-center gap-1 text-xs font-bold text-red-700 hover:text-red-900">
                            <XCircle className="w-3 h-3" />{lang === "ar" ? "رفض" : "Reject"}
                          </button>
                        </>
                      )}
                      {w.status === "approved" && (
                        <button onClick={() => transition(w, "paid")}
                                className="inline-flex items-center gap-1 text-xs font-bold text-green-700 hover:text-green-900"
                                data-testid={`wd-paid-${w.id}`}>
                          <CheckCircle2 className="w-3 h-3" />{lang === "ar" ? "تم الدفع" : "Mark paid"}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ── AUDIT LOG ───────────────────────────────────────────────────────────
function AuditPanel({ lang }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await apiClient.get("/admin/payment-audit", { params: { limit: 500 } });
        setRows(data.items || []);
      } finally { setLoading(false); }
    })();
  }, []);
  if (loading) return <div className="py-10 text-center text-[#6b6b6b]"><Loader2 className="w-5 h-5 animate-spin inline me-2" />{lang === "ar" ? "تحميل…" : "Loading…"}</div>;
  return (
    <div className="rounded-2xl border border-[#0a0a0a]/10 bg-white overflow-x-auto">
      <div className="p-3 text-xs text-[#6b6b6b] border-b border-[#0a0a0a]/10 inline-flex items-center gap-2">
        <ClipboardList className="w-3.5 h-3.5" />
        {lang === "ar" ? `${rows.length} حدث مسجّل` : `${rows.length} events logged`}
      </div>
      <table className="min-w-full text-xs">
        <thead className="bg-[#fafaf7] text-[10px] uppercase tracking-[0.2em] text-[#6b6b6b]">
          <tr>
            <th className="text-start p-2">{lang === "ar" ? "الوقت" : "Time"}</th>
            <th className="text-start p-2">{lang === "ar" ? "الفاعل" : "Actor"}</th>
            <th className="text-start p-2">{lang === "ar" ? "الحدث" : "Action"}</th>
            <th className="text-start p-2">{lang === "ar" ? "الكيان" : "Entity"}</th>
            <th className="text-start p-2">IP</th>
            <th className="text-start p-2">{lang === "ar" ? "بيانات" : "Payload"}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-b border-[#0a0a0a]/5">
              <td className="p-2 font-mono" dir="ltr">{r.at?.slice(0,19).replace("T"," ")}</td>
              <td className="p-2 font-mono">{(r.actor_user_id || "—").slice(0, 8)}</td>
              <td className="p-2 font-bold text-[#8a6f2e]">{r.action}</td>
              <td className="p-2 font-mono">{r.entity_type}:{(r.entity_id || "").slice(0,8)}</td>
              <td className="p-2 font-mono text-[10px]" dir="ltr">{r.ip || "—"}</td>
              <td className="p-2 font-mono text-[10px] max-w-xs truncate">{r.payload ? JSON.stringify(r.payload) : ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StatusPill({ status, lang }) {
  const m = {
    pending:   { c: "#c9a55c", ar: "قيد المراجعة", en: "Pending" },
    verified:  { c: "#22c55e", ar: "موثّق", en: "Verified" },
    rejected:  { c: "#ef4444", ar: "مرفوض", en: "Rejected" },
    suspended: { c: "#8b5cf6", ar: "معلّق", en: "Suspended" },
    approved:  { c: "#3b82f6", ar: "معتمد", en: "Approved" },
    paid:      { c: "#22c55e", ar: "مصروف", en: "Paid" },
  };
  const s = m[status] || { c: "#8a8a8a", ar: status, en: status };
  return (
    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold"
          style={{ backgroundColor: s.c + "22", color: s.c, border: `1px solid ${s.c}55` }}>
      {lang === "ar" ? s.ar : s.en}
    </span>
  );
}
